import { gql } from '@apollo/client';
import getAppUrl from './getAppUrl';
import getNodes from './getNodes';
import { objectToQueryString } from './querystring';

interface IGetOgImageProps {
  artifacts: any;
}

function getAbsoluteUrl(url: string) {
  // append a leading `/` if it's not set already in the url
  url += url.startsWith('/') ? '' : '/';
  return `${getAppUrl()}${url}`;
}

export function getPlpSeoTitleSuffix(hasRsvpCards: boolean) {
  return !hasRsvpCards
    ? 'Send online instantly | Track opens'
    : 'Send online instantly | RSVP tracking';
}

function getBreadcrumbs(navNodes: any[]) {
  const breadcrumbs = navNodes.map((navNode, index) => {
    const { seoContent, slug, type } = navNode;
    const url =
      seoContent && seoContent.customUrl ? seoContent.customUrl : `/cards/${type}/${slug}`;

    return {
      position: index + 2,
      name: navNode.title || navNode.fullTitle,
      item: getAbsoluteUrl(url),
    };
  });

  // append homepage the breadcrumbs always
  breadcrumbs.unshift({
    position: 1,
    name: 'Home',
    item: getAppUrl()!,
  });

  return breadcrumbs;
}

// Pulling props off the object.  We may want to use the `tab` prop as well.
export function getPLPCanonicalQuery({ page }: any) {
  let queryObject = { page: page > 1 ? page : undefined };

  return objectToQueryString(queryObject);
}

function getOgImage({ artifacts }: IGetOgImageProps) {
  const renditions = artifacts?.cardFront?.asset?.renditions;

  if (!renditions) return {};

  // This covers both the data available for PDPs and PLPs
  const { height, url: image, width } = renditions.staticJpeg || renditions.jpegMedium;

  return { image, width, height };
}

function getPlpSeoProps(cards: any, query: any) {
  // check if 25% of the cards are rsvp invitation cards
  const cardsList = getNodes<any>(cards);
  const rsvpCardsCount = cardsList.reduce((rsvpCardsCount, card) => {
    const eventType = card.package?.eventType || card.composition?.eventType;
    return eventType === 'rsvp_event' ? rsvpCardsCount + 1 : rsvpCardsCount;
  }, 0);
  const hasRsvpCards = (rsvpCardsCount / cardsList.length) * 100 >= 25;
  const seoTitle = cards?.meta?.seo?.title.trim() || cards?.meta?.context?.title.trim();

  const title = cards?.meta?.seo?.suffix
    ? cards?.meta?.seo?.suffix === 'default'
      ? `${seoTitle} | ${getPlpSeoTitleSuffix(hasRsvpCards)}`
      : `${seoTitle} ${cards?.meta?.seo?.suffix}`
    : `${seoTitle}`;

  const canonicalUrl = cards.meta.seo.canonicalUrl;
  const canonicalQuery = getPLPCanonicalQuery(query);

  let ogImage = {};
  // Find the first card that has the data we want
  // We should provide a generic branded fallback in the future...
  if (cardsList.length) {
    const ogImageCard = cardsList.find((ele) => ele?.composition?.artifacts);
    if (ogImageCard) {
      ogImage = getOgImage({ artifacts: ogImageCard?.composition?.artifacts });
    }
  }

  return {
    title,
    description: cards.meta.seo.metaDescription,
    canonicalUrl: canonicalUrl ? getAbsoluteUrl(canonicalUrl + canonicalQuery) : undefined,
    ogImage,
  };
}

function getCardPdpSeoProps(seoProps: any, originalCard: any): any {
  if (!seoProps) {
    return { title: '', description: '', canonicalUrl: '', breadcrumbs: [] };
  }

  const { canonicalUrl, metaDescription, metaTitle } = seoProps;
  let title,
    navNodes = [];
  const cardId = originalCard?.package?.id || originalCard?.composition?.packageId;

  if (originalCard) {
    title = `${originalCard.composition?.family?.meta?.label || metaTitle} | ${getPlpSeoTitleSuffix(
      originalCard?.composition?.eventType === 'rsvp_event'
    )}`;

    const {
      context,
      context: { group, section },
    } = originalCard.meta;

    navNodes = [section, group, context].filter(Boolean);
  }

  const breadcrumbs = getBreadcrumbs(navNodes);

  return {
    title: title || metaTitle,
    description: metaDescription,
    canonicalUrl: getAbsoluteUrl(canonicalUrl || `/cards/card/${cardId}`),
    breadcrumbs,
  };
}

function getFlyerPdpSeoProps(originalFlyer: any): any {
  const slug = originalFlyer?.slug;

  return {
    title: `${originalFlyer.name} | Send online instantly | Free tracking`,
    description: ``,
    canonicalUrl: getAbsoluteUrl(`/flyer/${slug}`),
  };
}

const GetPlpSeoPropsFragments = {
  cards: gql`
    fragment GetPlpSeoPropsCardsFragment on SearchResultConnection {
      edges {
        node {
          package {
            eventType
            carouselImageUrl
          }
          composition {
            eventType
          }
        }
      }
      meta {
        context {
          title
          fullTitle
          type
          slug
          seoContent {
            customUrl
          }
          section {
            title
            fullTitle
            type
            slug
            seoContent {
              customUrl
            }
          }
          group {
            title
            fullTitle
            type
            slug
            seoContent {
              customUrl
            }
          }
        }
        seo {
          canonicalUrl
          description
          metaDescription
          metaTitle
          title
          suffix
        }
      }
    }
  `,
};

const GetCardPDPSeoPropsFragments = gql`
  fragment GetCardPDPSeoPropsFragment on CardDetail {
    package {
      eventType
    }
    composition {
      eventType
    }
    meta {
      context {
        title
        fullTitle
        type
        slug
        seoContent {
          customUrl
        }
        section {
          title
          fullTitle
          type
          slug
          seoContent {
            customUrl
          }
        }
        group {
          title
          fullTitle
          type
          slug
          seoContent {
            customUrl
          }
        }
      }
      seo {
        canonicalUrl
        metaDescription
        metaTitle
      }
    }
  }
`;

export {
  getPlpSeoProps,
  getCardPdpSeoProps,
  getFlyerPdpSeoProps,
  getOgImage,
  GetPlpSeoPropsFragments,
  GetCardPDPSeoPropsFragments,
};
